import type { TFunction } from 'i18next';
import { erSpeil, type Arealplan, type Planforhold } from '@origo/planregister';
import type { RelationPair } from '../types';
import { forholdtypeBadge } from '../codelists/forholdtype';
import { formatDateShortNb } from './formatDate';

interface PartyLookup {
    (napId: string): { plannavn: string; planId: string };
}

export function toRelationPair(
    relation: Planforhold,
    lookupParty: PartyLookup,
    t: TFunction
): RelationPair {
    const fromParty = lookupParty(relation.nasjonalArealplanIdFra);
    const toParty = lookupParty(relation.nasjonalArealplanIdTil);
    return {
        id: relation.relasjonId,
        from: fromParty,
        to: toParty,
        vedtakEndeligPlanDato: formatDateShortNb(relation.vedtakEndeligPlanDato) ?? '—',
        begrunnelse: relation.begrunnelse || undefined,
        type: forholdtypeBadge(relation.forholdtype, t),
    };
}

/**
 * Relasjonene som skal vises for planen: alle der planen er «Fra», pluss
 * «Til»-relasjoner som mangler speil. Par (A→B + speil B→A) vises dermed én
 * gang, mens ensrettede relasjoner (legacy-data, eller registrert uten
 * motsatt type i gammel redaktør) ikke forsvinner.
 */
export function visibleRelations(all: Planforhold[], napId: string): Planforhold[] {
    return all.filter(rel =>
        rel.nasjonalArealplanIdFra === napId
        || (rel.nasjonalArealplanIdTil === napId && !all.some(other => erSpeil(other, rel)))
    );
}

export function buildPartyLookup(plans: Arealplan[]): PartyLookup {
    const map = new Map<string, { plannavn: string; planId: string }>();
    for (const plan of plans) {
        map.set(plan.nasjonalArealplanId, { plannavn: plan.plannavn, planId: plan.planId });
    }
    return (napId: string) => map.get(napId) ?? { plannavn: napId, planId: napId };
}
